import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// ── Storage keys ─────────────────────────────────────────────────────────────
const ACCESS_TOKEN_KEY  = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

// ── Helpers ──────────────────────────────────────────────────────────────────

const API_BASE = process.env.REACT_APP_API_BASE_URL
  ? `${process.env.REACT_APP_API_BASE_URL}/api/auth`
  : '/api/auth';

/** Decode the payload of a JWT without verifying the signature. */
function decodeJWT(token) {
  try {
    const payload = token.split('.')[1];
    const padded   = payload + '='.repeat((4 - payload.length % 4) % 4);
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

/** True if the token exists, has not expired, and is a local-auth JWT (has role field). */
function isTokenValid(token) {
  if (!token) return false;
  const payload = decodeJWT(token);
  // Must have exp and role — old Cognito tokens lack 'role', treat them as invalid
  if (!payload?.exp || !payload?.role) return false;
  return payload.exp * 1000 > Date.now() + 30_000;
}

// ── Context ───────────────────────────────────────────────────────────────────

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

/**
 * Returns the "locked" establishment ID for the current user:
 *   MultiClientAdmin → user.activeEstablishmentId (switches via OrgSwitcher)
 *   ClientAdmin      → user.establishment_id (fixed single org)
 *   SuperAdmin/other → null (no lock; sees all orgs)
 * Always returns a string or null.
 */
export const useActiveEstId = () => {
  const { user } = useAuth();
  if (!user) return null;
  if (user.role === 'MultiClientAdmin') {
    return user.activeEstablishmentId ? String(user.activeEstablishmentId) : null;
  }
  return user.establishment_id ? String(user.establishment_id) : null;
};

// ── Provider ──────────────────────────────────────────────────────────────────

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  // ------------------------------------------------------------------
  // Build user object from a decoded JWT payload
  // ------------------------------------------------------------------
  const buildUser = (payload, activeEstId = null) => {
    const estIds = payload.establishment_ids ?? null;   // MultiClientAdmin only
    // Active establishment: use provided override, else first in list, else single est_id
    const activeEst = activeEstId
      ?? (estIds && estIds.length > 0 ? estIds[0] : null)
      ?? payload.establishment_id
      ?? null;
    return {
      username:               payload.username,
      email:                  payload.email,
      role:                   payload.role,
      roles:                  [payload.role],
      userId:                 payload.sub,
      establishment_id:       payload.establishment_id ?? null,  // single org (ClientAdmin) or null
      establishment_ids:      estIds,                            // multi-org list (MultiClientAdmin)
      activeEstablishmentId:  activeEst,                         // currently selected org
      establishment_name:     null,                              // filled in by effect below
    };
  };

  // ------------------------------------------------------------------
  // Silently refresh tokens using the stored refresh token
  // ------------------------------------------------------------------
  const silentRefresh = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (!refreshToken) return false;

    try {
      const res = await fetch(`${API_BASE}/refresh`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ refresh_token: refreshToken }),
      });
      if (!res.ok) return false;

      const data = await res.json();
      localStorage.setItem(ACCESS_TOKEN_KEY,  data.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);
      const payload = decodeJWT(data.access_token);
      if (payload) setUser(buildUser(payload));
      return true;
    } catch {
      return false;
    }
  }, []);

  // ------------------------------------------------------------------
  // Check auth state on mount — restore session from localStorage
  // ------------------------------------------------------------------
  useEffect(() => {
    const restore = async () => {
      try {
        const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);

        if (isTokenValid(accessToken)) {
          // Token is fresh — restore user directly from JWT payload
          const payload = decodeJWT(accessToken);
          if (payload) {
            setUser(buildUser(payload));
            return;
          }
        }

        // Access token expired — try silent refresh
        const refreshed = await silentRefresh();
        if (!refreshed) {
          localStorage.removeItem(ACCESS_TOKEN_KEY);
          localStorage.removeItem(REFRESH_TOKEN_KEY);
          setUser(null);
        }
      } catch {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    restore();
  }, [silentRefresh]);

  // ------------------------------------------------------------------
  // Fetch establishment name for the active establishment
  // ------------------------------------------------------------------
  useEffect(() => {
    const activeId = user?.activeEstablishmentId;
    if (!activeId) return;
    const token = localStorage.getItem(ACCESS_TOKEN_KEY);
    if (!token) return;
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';
    fetch(`${API_BASE_URL}/api/establishment`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const list = data?.establishments || [];
        const match = list.find(e => e.id === activeId);
        if (match?.name) {
          setUser(prev => prev ? { ...prev, establishment_name: match.name } : prev);
        }
      })
      .catch(() => {});
  }, [user?.activeEstablishmentId]);

  // ------------------------------------------------------------------
  // Proactive token refresh: re-run silent refresh 5 min before expiry
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!user) return;

    const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
    const payload     = decodeJWT(accessToken);
    if (!payload?.exp) return;

    const msUntilExpiry = payload.exp * 1000 - Date.now();
    const refreshAt     = msUntilExpiry - 5 * 60 * 1000;   // 5 min before expiry
    if (refreshAt <= 0) return;

    const timer = setTimeout(silentRefresh, refreshAt);
    return () => clearTimeout(timer);
  }, [user, silentRefresh]);

  // ------------------------------------------------------------------
  // Switch active establishment (MultiClientAdmin only)
  // ------------------------------------------------------------------
  const setActiveEstablishment = (establishmentId) => {
    setUser(prev => {
      if (!prev) return prev;
      const allowed = prev.establishment_ids;
      if (allowed && !allowed.includes(establishmentId)) return prev;
      return { ...prev, activeEstablishmentId: establishmentId, establishment_name: null };
    });
  };

  // ------------------------------------------------------------------
  // Login
  // ------------------------------------------------------------------
  const login = async (username, password) => {
    try {
      setError(null);
      const res = await fetch(`${API_BASE}/login`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ username, password }),
      });

      const data = await res.json();

      if (!res.ok) {
        const raw = data.detail;
        const msg = Array.isArray(raw)
          ? raw.map(e => e.msg || JSON.stringify(e)).join('; ')
          : (typeof raw === 'string' ? raw : data.message || 'Login failed');
        setError(msg);
        return { success: false, message: msg };
      }

      localStorage.setItem(ACCESS_TOKEN_KEY,  data.access_token);
      localStorage.setItem(REFRESH_TOKEN_KEY, data.refresh_token);

      const payload = decodeJWT(data.access_token);
      setUser(buildUser(payload));
      return { success: true };
    } catch (err) {
      const msg = err.message || 'Unexpected error during login';
      setError(msg);
      return { success: false, message: msg };
    }
  };

  // ------------------------------------------------------------------
  // Logout
  // ------------------------------------------------------------------
  const logout = async () => {
    try {
      const accessToken = localStorage.getItem(ACCESS_TOKEN_KEY);
      if (accessToken) {
        // Best-effort server-side token invalidation
        await fetch(`${API_BASE}/logout`, {
          method:  'POST',
          headers: { Authorization: `Bearer ${accessToken}` },
        }).catch(() => {});
      }
    } finally {
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      setUser(null);
    }
  };

  // ------------------------------------------------------------------
  // Exposed helpers for apiService.js
  // ------------------------------------------------------------------

  /** Returns the stored access token string (or null). */
  const getAccessToken = () => localStorage.getItem(ACCESS_TOKEN_KEY);

  // ------------------------------------------------------------------
  // Context value
  // ------------------------------------------------------------------
  const value = {
    user,
    loading,
    error,
    login,
    logout,
    getAccessToken,
    setActiveEstablishment,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
